import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useFirestoreDoc from "../../../app/hooks/useFirestoreDoc";
import { listenToDocFromFirestore } from "../../../app/firestore/firestoreService";
import { Redirect } from "react-router-dom";
import { Grid, Loader, Popup } from "semantic-ui-react";
import { TEAL_BOX } from "../../../css/styles";
import { EMPLOYER_DASHBOARD_MANAGE_JOBS_PATH } from "../../../app/api/routeConstants";
import sortBy from "lodash/sortBy";
import groupBy from "lodash/groupBy";
import mapKeys from "lodash/mapKeys";
import { Bar } from "react-chartjs-2";
import { format } from "date-fns";
import ReactToPrint from "react-to-print";
import print from "../../../assets/icons/print.svg";
import { getJobTitleAndCompanyHeading } from "../../../js/helpers/getJobTitleAndCompanyHeading";
import {
  FIRESTORE_JOBS_STATUS_LOG_COLLECTION,
  FIRESTORE_VIEWS_COLLECTION,
  QUERY_CODES,
} from "../../../app/firestore/firestoreConstants";
import StatisticWithText from "../../../app/common/components/StatisticWithText";
import Statistic from "../../../app/common/components/Statistic";
import BackButton from "../../../app/common/components/BackButton";

export default function JobStatsPane({ handleBackToManage, job }) {
  const dispatch = useDispatch();

  const printRef = useRef();

  const { currentUserAccount } = useSelector((state) => state.account);
  const { loading, error } = useSelector((state) => state.async);
  const { responsive } = useSelector((state) => state.app);

  const [jobStatusLog, setJobStatusLog] = useState(null);
  const [viewCountForJob, setViewCountForJob] = useState(null);

  const jobRef = job.refNumber || "";

  useFirestoreDoc({
    shouldExecute: jobRef !== "",
    query: () =>
      listenToDocFromFirestore({
        dbCollection: FIRESTORE_VIEWS_COLLECTION,
        id: jobRef,
      }),
    data: (viewCount) => setViewCountForJob(viewCount),
    deps: [jobRef, dispatch],
    customErrorMessage: "Unable to fetch views count",
    queryCode: QUERY_CODES.listenToJobViewCountFromFirestore,
  });

  useFirestoreDoc({
    shouldExecute: jobRef !== "",
    query: () =>
      listenToDocFromFirestore({
        dbCollection: FIRESTORE_JOBS_STATUS_LOG_COLLECTION,
        id: jobRef,
      }),
    data: (logHistory) => setJobStatusLog(logHistory),
    deps: [jobRef, dispatch],
    customErrorMessage: "Job Status History not found",
    queryCode: QUERY_CODES.listenToServiceStatusLogFromFirestore,
  });

  function getData() {
    if (!viewCountForJob) return { x: [], y: [] };
    let daysX = [];
    let viewCountY = [];

    const sortedViaDate = sortBy(viewCountForJob.views, "dateViewed");

    let groupedResults = groupBy(sortedViaDate, (result) =>
      format(result.dateViewed.toDate(), "dd/MM")
    );

    mapKeys(groupedResults, function (value, key) {
      daysX.push(key);
      viewCountY.push(value.length);
    });

    return { x: daysX, y: viewCountY };
  }

  // redirect if not authorised before show loading
  if (
    viewCountForJob &&
    !currentUserAccount.isAdmin &&
    currentUserAccount.id !== viewCountForJob.employerId
  ) {
    return <Redirect to={EMPLOYER_DASHBOARD_MANAGE_JOBS_PATH} />;
  }

  if (
    loading ||
    !currentUserAccount ||
    !jobStatusLog ||
    (!viewCountForJob && !error)
  ) {
    return <Loader active={true} content="Loading job statistics" />;
  } else if (error) {
    return <Redirect to="/error" />;
  }

  const data = {
    labels: getData().x,
    datasets: [
      {
        label: "Unique Views",
        data: getData().y,
        backgroundColor: ["rgba(85, 166, 202, 1)"],
        borderColor: ["rgba(54, 162, 235, 1)"],
        borderWidth: 1,
      },
    ],
  };

  const options = {
    ticks: {
      precision: 0,
    },
  };

  const UNIQUE_VIEWS = (
    <div>
      <div>Track views more accurately with a view count you can trust.</div>
      <br />
      <Popup
        className="popup-container"
        position={responsive.isMobile ? "bottom right" : "bottom left"}
        content={
          <div
            className="popup-container-content body-blue-text"
            style={responsive.isMobile ? { width: 250 } : {}}
          >
            <div className="h3-text">How we count job views</div>
            <div style={{ margin: "12px 0" }}>
              Repeat views by the same user within a 24-hour period, are counted
              as 1 view.
            </div>
            <div>
              <span className="semi-bold">Example:</span> If a user refreshes
              their browser page or clicks multiple times on your job post
              within 24-hours, these actions will not inflate your view count.
            </div>
          </div>
        }
        on={"hover"}
        trigger={
          <span className="action-text teal-colour">How we count views</span>
        }
      />
    </div>
  );

  return (
    <div ref={printRef} className="print-padding">
      <div className="hello-radius-logo-print">
        <img
          style={{ height: 100 }}
          src={
            "https://firebasestorage.googleapis.com/v0/b/helloradius-caf9d.appspot.com/o/photos%2Flogohelloradiusname.png?alt=media&token=c4740341-8645-4eed-ab0a-047f219d5a48"
          }
          alt="hello radius logo"
        />
      </div>
      <BackButton action={handleBackToManage} />
      <h2>
        Statistics
        <span style={{ fontSize: 19, fontWeight: 500, margin: "0 4px" }}>
          {" • "}
        </span>
        <span>
          {getJobTitleAndCompanyHeading({
            title: job.title,
            company: job.company,
          })}
        </span>
      </h2>
      {viewCountForJob !== null ? (
        <Grid stackable>
          <Grid.Row>
            <Grid.Column width={12}>
              <div style={{ margin: "14px 0" }}>
                <span style={{ fontSize: 14, fontWeight: 400 }}>
                  REF: {jobRef}
                </span>
              </div>

              <ReactToPrint
                trigger={() => (
                  <div className="print-button clickable">
                    <span>
                      <img className="print-icon" src={print} alt="menu" />
                    </span>
                  </div>
                )}
                content={() => printRef.current}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row style={{ marginTop: 4 }}>
            <Grid.Column width={9} textAlign="center">
              <StatisticWithText
                heading="Unique Views"
                statFontSize={36}
                stat={viewCountForJob.viewsCount}
                text={UNIQUE_VIEWS}
              />
            </Grid.Column>
            {job.contactWebsiteUrl.url && job.contactWebsiteUrl.isApply && (
              <Grid.Column width={4}>
                <Statistic
                  style={{ height: "100%" }}
                  headingTextClass={"h3-text"}
                  heading="Apply via website clicks"
                  stat={
                    typeof viewCountForJob.applyOnWebsiteClicks === "number"
                      ? viewCountForJob.applyOnWebsiteClicks
                      : "n/a"
                  }
                />
              </Grid.Column>
            )}
          </Grid.Row>
          <Grid.Row
            style={{ margin: "10px 0" }}
            className="job-stats-bar-chart"
          >
            <Grid.Column width={16}>
              {viewCountForJob.viewsCount > 0 && (
                <div style={TEAL_BOX}>
                  <div
                    className="body-blue-text"
                    style={{ padding: "0 0 15px 10px" }}
                  >
                    <div>Unique views per day*</div>
                  </div>
                  <Bar
                    data={data}
                    options={options}
                    type="bar"
                    style={{ maxHeight: "45vh" }}
                  />
                  <div style={{ fontSize: 13, padding: "12px 0 4px 10px" }}>
                    *Days with 0 views are not visible.
                  </div>
                </div>
              )}
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            {jobStatusLog && (
              <Grid.Column width={8}>
                <div
                  className="body-blue-text"
                  style={{ ...TEAL_BOX, width: "100%" }}
                >
                  <h3>Job Status History</h3>
                  {sortBy(jobStatusLog.log, "date").map((log, index) => (
                    <div key={index}>
                      <span className="semi-bold">{log.status}: </span>
                      <span>
                        {format(
                          log.date.toDate(),
                          "dd MMMM yyyy - 'Time:' HH:mm"
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </Grid.Column>
            )}
          </Grid.Row>
        </Grid>
      ) : (
        <div className="width-90">
          <br />
          <div className={"h3-text"}>
            We're sorry, the statistics of the job you are looking for can't be
            found.
          </div>
        </div>
      )}
    </div>
  );
}
